import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Row, Col, Form, Tabs, Button } from "antd";
import { useLocation } from "react-router-dom";
import CreatePostForm from "./ChannelComponents/CreatePostForm";
import PostViewScreen from "./ChannelComponents/PostViewScreen";
import { UserInfoContext } from "Context/CommonContext";
import { useChannelsPost } from "./useChannelsPost";
import SubjectSidebar from "./ChannelComponents/SubjectSidebar";
import { POST_TYPES } from "./ChannelComponents/ChannelFormComponents/PostDetails";
import RecentPosts from "./ChannelComponents/RecentPosts";
import { BASE_URL } from "utilFunctions/Constants/urlConstants";
import dayjs, { Dayjs } from "dayjs";
import PostSettingsDrawer from "Pages/MyBatches/Channels/ChannelComponents/PostSettingsDrawer";
import { pushToDataLayerForGTM } from "utilFunctions/helperFunctions/analytics";

const ChannelsPostHome = ({
  batchDetailsData,
  batchId: propBatchId,
  selectedSubject: selectedSubjectFromParent,
}: any) => {
  const location = useLocation();
  const userInfo = useContext<any>(UserInfoContext);
  const [selectedSubject, setSelectedSubject] = useState(
    selectedSubjectFromParent || "General"
  );
  const [activeTab, setActiveTab] = useState(POST_TYPES[0].value);
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [isViewingPost, setIsViewingPost] = useState(false);
  const [form] = Form.useForm();
  const [isPublishDrawerVisible, setIsPublishDrawerVisible] = useState(false);
  const [scheduleOption, setScheduleOption] = useState("now");
  const [scheduleTime, setScheduleTime] = useState<Dayjs | null>(null);
  useEffect(() => {
    setSelectedSubject(selectedSubjectFromParent || "General");
  }, [selectedSubjectFromParent]);
  const allowedRoles = [
    "Faculty",
    "Branch EDP",
    "Academic Head",
    "Regional Head",
    "National Head",
    "NAD Team",
    "Academic Counsellor",
  ];
  const userRoles = userInfo?.fexRole || [];
  const hasFullAccess = Array.isArray(userRoles)
    ? userRoles.some((role) => allowedRoles.includes(role))
    : allowedRoles.includes(userRoles as unknown as string);
  const batchId = Number(propBatchId ?? location.pathname.split("/")[3]);
  const batchName = batchDetailsData?.batchName;
  const batchInfo = {
    value: batchId,
    label: batchName,
  };
  const {
    posts,
    setPosts,
    postsLoading,
    fetchPosts,
    hasMore,
    next,
    // createPost,
    createPostLoading,
    // updatePost,
    updatePostLoading,
    updatePostPublish,
    deletePost,
    deleteAttachment,
    getSinglePost,
  } = useChannelsPost(form);

  const fetchPostsRef = useRef(fetchPosts);
  useEffect(() => {
    fetchPostsRef.current = fetchPosts;
  });

  const refreshPosts = () => {
    const url = `${BASE_URL}post/getAllPosts?batchId=${batchId}&postType=${activeTab}&subject=${selectedSubject}`;
    fetchPosts(url, true);
  };

  useEffect(() => {
    const url = `${BASE_URL}post/getAllPosts?batchId=${batchId}&postType=${activeTab}&subject=${selectedSubject}`;
    fetchPostsRef.current(url, true);
  }, [batchId, activeTab, selectedSubject]);

  useEffect(() => {
    if (batchDetailsData?.academicGroup) {
      pushToDataLayerForGTM({
        event: "ag_channels_channels_batch_detail",
        BATCH_ID: batchDetailsData?.batchName,
        TAB_NAME: activeTab,
        EMPLOYEE_ID: userInfo?.employeeId,
        USER_ROLE: userInfo?.fexRole,
        SUBJECT: selectedSubject,
        STREAM: batchDetailsData?.academicGroup,
      });
    }
  }, [batchDetailsData, selectedSubject]);

  useEffect(() => {
    form.setFieldsValue({ subject: selectedSubject });
  }, [selectedSubject]);

  const handleSubjectSelect = (subject: string) => {
    setSelectedSubject(subject);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const handleCreatePost = () => {
    form.resetFields();
    setSelectedPost(() => null);
    setIsCreatingPost(true);
    setIsViewingPost(false);
    pushToDataLayerForGTM({
      event: "ag_channels_new_communication_created",
      BATCH_ID: batchDetailsData?.batchName,
      TAB_NAME: activeTab,
      EMPLOYEE_ID: userInfo?.employeeId,
      USER_ROLE: userInfo?.fexRole,
      SUBJECT: selectedSubject,
      STREAM: batchDetailsData?.academicGroup,
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setIsCreatingPost(false);
    setIsViewingPost(false);
    setSelectedPost(() => null);
    refreshPosts();
  };

  const handlePostSelect = (post: any) => {
    getSinglePost(post.id, (fullPost) => {
      setSelectedPost(() => fullPost);
      setIsViewingPost(true);
      setIsCreatingPost(false);
    });
  };

  const handleEditPost = (post: any) => {
    getSinglePost(post.id, (fullPost) => {
      setSelectedPost(() => fullPost);
      setIsViewingPost(false);
      setIsCreatingPost(true);
    });
  };

  const handleIncrementCommentCount = (postId: string) => {
    setPosts(
      posts.map((p: any) =>
        p.id == postId ? { ...p, commentCount: p.commentCount + 1 } : p
      )
    );
  };
  const handleDecrementCommentCount = (postId: string) => {
    setPosts(
      posts.map((p: any) =>
        p.id == postId
          ? { ...p, commentCount: Math.max(0, p.commentCount - 1) }
          : p
      )
    );
  };
  const handleDeletePost = (postId: string) => {
    deletePost(postId, () => {
      setPosts(posts.filter((p: any) => p.id != postId));
    });
  };
  const nextPosts = useCallback(() => {
    const url = `${BASE_URL}post/getAllPosts?batchId=${batchId}&postType=${activeTab}&subject=${selectedSubject}`;
    next(url);
  }, [batchId, activeTab, selectedSubject, next]);

  // const handleDelete = (postId: string) => {
  //   deletePost(postId, () => {
  //     refreshPosts();
  //     handleCancel();
  //   });
  // };

  const handleDeleteAttachment = (attachmentId: string) => {
    if (selectedPost && selectedPost.content) {
      deleteAttachment(attachmentId, selectedPost.id, () => {
        setTimeout(() => {
          const updatedContent = selectedPost.content.filter(
            (attachment: any) => attachment.id !== attachmentId
          );
          setSelectedPost({
            ...selectedPost,
            content: updatedContent,
          });
        }, 500);
      });
    }
  };

  const handleSaveSuccess = (savedPost: any) => {
    console.log("Post saved successfully:", savedPost);
    const postId = savedPost?.id || savedPost;
    if (postId) {
      getSinglePost(postId, (fullPost: any) => {
        setSelectedPost(fullPost);
      });
    }
    refreshPosts();
  };
  const handlePublish = () => {
    form
      .validateFields()
      .then((values) => {
        console.log("Validated values for publish:", values);
        if (selectedPost?.id && selectedPost?.scheduledAt) {
          setScheduleOption("later");
          setScheduleTime(dayjs(selectedPost.scheduledAt));
        } else {
          setScheduleOption("now");
          setScheduleTime(null);
        }
        setIsPublishDrawerVisible(true);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleConfirmPublish = async () => {
    const finalPayload: any = {
      postId: selectedPost.id,
    };

    if (scheduleOption === "later" && scheduleTime) {
      finalPayload.scheduleTime = scheduleTime.format("YYYY-MM-DDTHH:mm:ssZ");
    } else {
      finalPayload.scheduleTime = null;
    }
    await updatePostPublish(BASE_URL + "post/publish", finalPayload);
    pushToDataLayerForGTM({
      event: "ag_channels_published_communication_created",
      BATCH_ID: batchDetailsData?.batchName,
      TAB_NAME: activeTab,
      EMPLOYEE_ID: userInfo?.employeeId,
      USER_ROLE: userInfo?.fexRole,
      SUBJECT: selectedSubject,
      STREAM: batchDetailsData?.academicGroup,
      TITLE: selectedPost?.title,
      CONTENT: selectedPost?.description,
      ATTACHMENTS: selectedPost?.content?.length || 0,
      TARGET_BATCHES: selectedPost?.batchIds?.length || 0,
      IS_POST: String(selectedPost?.postType === "POST"),
      VISIBILITY_TOGGLE: String(selectedPost?.commentVisibility),
      IS_SCHEDULED: String(scheduleOption === "later"),
    });
    setTimeout(() => {
      refreshPosts();
    }, 500);
    setIsCreatingPost(false);
    setIsPublishDrawerVisible(false);
  };
  return (
    <Row>
      <Col xs={24} sm={24} md={4} lg={4} xl={4}>
        <SubjectSidebar
          batchDetailsData={batchDetailsData}
          selectedSubject={selectedSubject}
          onSubjectSelect={handleSubjectSelect}
        />
      </Col>
      <Col xs={24} sm={24} md={20} lg={20} xl={20}>
        {isCreatingPost ? (
          <CreatePostForm
            key={selectedPost?.id || "new"}
            form={form}
            handleCancel={handleCancel}
            onSaveSuccess={handleSaveSuccess}
            handlePublish={handlePublish}
            initialValues={selectedPost}
            isUpdating={createPostLoading || updatePostLoading}
            hasFullAccess={hasFullAccess}
            batchId={batchId}
            batchInfo={batchInfo}
            batchDetailsData={batchDetailsData}
            initialBatchOptions={selectedPost?.initialBatchOptions}
            onDeleteAttachment={handleDeleteAttachment}
            activeTab={activeTab}
            selectedSubject={selectedSubject}
          />
        ) : (
          <Tabs
            type="card"
            activeKey={activeTab}
            onChange={handleTabChange}
            tabBarStyle={{
              padding: "0 16px",
              height: "100%",
              backgroundColor: "transparent",
            }}
            tabBarExtraContent={
              hasFullAccess && (
                <Button type="primary" onClick={handleCreatePost}>
                  + Create New
                </Button>
              )
            }
          >
            {POST_TYPES.map((tab) => (
              <Tabs.TabPane
                tab={tab.label}
                key={tab.value}
                style={{
                  backgroundColor: "#fff",
                }}
              >
                <div
                  id={`scrollableDiv-${tab.value}`}
                  style={{
                    padding: 16,
                    height: "calc(100vh - 120px)",
                    overflowY: "auto",
                  }}
                >
                  <RecentPosts
                    posts={posts}
                    handlePostSelect={handlePostSelect}
                    handleEditPost={handleEditPost}
                    handleDeletePost={handleDeletePost}
                    handleIncrementCommentCount={handleIncrementCommentCount}
                    handleDecrementCommentCount={handleDecrementCommentCount}
                    loading={postsLoading}
                    hasMore={hasMore}
                    next={nextPosts}
                    hasFullAccess={hasFullAccess}
                    scrollableTarget={`scrollableDiv-${tab.value}`}
                  />{" "}
                </div>
              </Tabs.TabPane>
            ))}
          </Tabs>
        )}
        {selectedPost && isViewingPost && (
          <PostViewScreen
            selectedPost={selectedPost}
            isPostLoading={false}
            onDelete={() => handleDeletePost(selectedPost.id)}
            onCancel={handleCancel}
            hasFullAccess={hasFullAccess}
          />
        )}
      </Col>
      <PostSettingsDrawer
        open={isPublishDrawerVisible}
        onClose={() => {
          setScheduleTime(null);
          setIsPublishDrawerVisible(false);
        }}
        onConfirm={handleConfirmPublish}
        scheduleOption={scheduleOption}
        onScheduleChange={(e) => setScheduleOption(e.target.value)}
        scheduleTime={scheduleTime}
        onScheduleTimeChange={setScheduleTime}
      />
    </Row>
  );
};

export default ChannelsPostHome;
